import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSwipeable } from "react-swipeable";
import arrowIcon from "../assets/button-arrow.svg";
import { useNavigate } from "react-router-dom";

const Business = ({ screenInfo }) => {
  const { isDesktopModeOnMobile, width } = screenInfo;
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth >= 768 && window.innerWidth < 1280
  );

  const navigate = useNavigate();

  useEffect(() => {
    const updateSize = () => {
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1280);
    };
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const cards = [
    "I’m a client looking for expert web/app developers to build innovative digital solutions!",
    "I’m a tech enthusiast looking to join your team and work on exciting projects!",
    "I’m an affiliate marketer/sales partner excited to collaborate and grow together!",
  ];

  const maxIndex = isTablet ? 1 : 2;

  const paginate = (newDirection) => {
    if (animating) return;

    setAnimating(true);
    setDirection(newDirection);
    setIndex((prev) => {
      if (newDirection === "left") {
        return prev === maxIndex ? 0 : prev + 1;
      } else {
        return prev === 0 ? maxIndex : prev - 1;
      }
    });
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => paginate("left"),
    onSwipedRight: () => paginate("right"),
    trackMouse: true,
    delta: 10,
  });

  const variants = {
    enter: (dir) => ({
      x: dir === "left" ? 40 : -40,
      opacity: 0,
      scale: 0.95,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (dir) => ({
      x: dir === "left" ? -40 : 40,
      opacity: 0,
      scale: 0.95,
    }),
  };

  const handleKnowMore = () => {
    navigate("/contact");
  };

  return (
    <div
      className={`${ isDesktopModeOnMobile && width > 768   ? "":"lg:min-h-screen  "} w-full  flex  justify-center 2xl:items-center lg:pt-[8rem]  pt-[6rem] sm:py-[8rem]    px-4 sm:px-6 md:px-6 pb-10 lg:pb-[4.5rem]  `}
    >
      <div className="flex flex-col gap-6 sm:gap-8 text-white w-full max-w-screen-md md:max-w-[90vw] lg:max-w-[80vw] xl:max-w-[80vw] 2xl:max-w-[70vw]">
        {/* Heading Section */}
        <div className="flex flex-col gap-4 sm:gap-6 items-center text-center">
          <h1 className="poppins-semibold text-2xl sm:text-3xl md:text-4xl">
            Let’s Talk Business
          </h1>
          <p className="poppins-regular text-center text-sm sm:text-base md:text-lg md:text-left leading-[1.8rem] sm:leading-[1.8rem] md:leading-[2rem] lg:leading-[2.2rem] tracking-wide opacity-90 px-2 sm:px-4 md:px-0">
            Whether you're looking for tech solutions, collaboration, or expert
            guidance, we're here to help. Reach out to us, and let's explore how
            Optimum Codes can drive your business forward. Let’s build something
            great together!
          </p>
        </div>

        {/* Desktop View */}
        <div className="hidden xl:flex justify-center mt-16 gap-6 lg:gap-8">
          {cards.map((text, i) => (
            <div
              key={i}
              className="w-full max-w-[400px] bg-white bg-opacity-5 backdrop-blur-md rounded-2xl p-6 text-white shadow-lg border border-white border-opacity-20 flex flex-col justify-center items-center transition-transform duration-300 ease-in-out hover:scale-105 cursor-pointer"
            >
              <p className="text-white poppins-regular text-base text-center">
                {text}
              </p>
              <button
                className="bg-gradient-to-r from-[#5637B2] to-[#6340CD] py-4 w-[200px] rounded-xl mt-8 flex justify-center items-center"
                onClick={handleKnowMore}
              >
                <p className="text-lg">Know More</p>
                <img
                  src={arrowIcon}
                  alt="Arrow Icon"
                  className="w-5 h-5 ml-4"
                />
              </button>
            </div>
          ))}
        </div>

        {/* Tablet View (2 Cards) */}
        {isTablet && (
          <div className="w-full flex justify-center mt-8">
            <AnimatePresence
              custom={direction}
              mode="wait"
              onExitComplete={() => setAnimating(false)}
            >
              <motion.div
                key={index}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4 }}
                className="flex gap-8 w-[80vw] justify-center"
                {...handlers}
              >
                {[0, 1].map((offset) => {
                  const cardIndex = (index + offset) % cards.length;
                  return (
                    <div
                      key={cardIndex}
                      className="w-[45%] max-w-[320px] bg-white bg-opacity-5 backdrop-blur-md rounded-2xl p-6 text-white shadow-lg border border-white border-opacity-20 flex flex-col justify-center items-center transition-transform duration-300 ease-in-out cursor-pointer"
                    >
                      <p className="text-white poppins-regular text-base text-center">
                        {cards[cardIndex]}
                      </p>
                      <button
                        className="bg-gradient-to-r from-[#5637B2] to-[#6340CD] py-4 w-[200px] rounded-xl mt-8 flex justify-center items-center"
                        onClick={handleKnowMore}
                      >
                        <p className="text-lg">Know More</p>
                        <img
                          src={arrowIcon}
                          alt="Arrow Icon"
                          className="w-5 h-5 ml-4"
                        />
                      </button>
                    </div>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {/* Mobile View (1 Card, Swipeable) */}
        {!isTablet && (
          <div className="md:hidden w-full flex flex-col items-center justify-center mt-8">
            <AnimatePresence
              custom={direction}
              mode="wait"
              onExitComplete={() => setAnimating(false)}
            >
              <motion.div
                key={index}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4 }}
                className="w-[70vw]  flex justify-center h-[15rem]"
                {...handlers}
              >
                <div className="w-full max-w-[350px] bg-white bg-opacity-5 backdrop-blur-md rounded-2xl p-6 text-white shadow-lg border border-white border-opacity-20 flex flex-col justify-center items-center transition-transform duration-300 ease-in-out cursor-pointer">
                  <p className="text-white poppins-regular text-base text-center">
                    {cards[index]}
                  </p>
                  <button
                    className="bg-gradient-to-r from-[#5637B2] to-[#6340CD] py-4 w-[200px] rounded-xl mt-8 flex justify-center items-center"
                    onClick={handleKnowMore}
                  >
                    <p className="text-lg">Know More</p>
                    <img
                      src={arrowIcon}
                      alt="Arrow Icon"
                      className="w-5 h-5 ml-4"
                    />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {/* Pagination Dots */}
        <div className="flex justify-center mt-6 xl:hidden">
          {(isTablet ? [0, 1] : [0, 1, 2]).map((i) => (
            <div
              key={i}
              className={`mx-1 rounded-full transition-all duration-300 cursor-pointer ${
                index === i ? "w-5 h-3 bg-white" : "w-2 h-2 bg-[#6340CD]"
              }`}
              onClick={() => paginate(i > index ? "left" : "right")}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Business;

// const cards = [
//   "I’m a client looking for expert web/app developers to build innovative digital solutions!",
//   "I’m a tech enthusiast looking to join your team and work on exciting projects!",
//   "I’m an affiliate marketer/sales partner excited to collaborate and grow together!",
// ];
